import { zodResolver } from "@hookform/resolvers/zod"
import { Baby, KeyRound } from "lucide-react"
import { type ReactNode, useState } from "react"
import { useForm } from "react-hook-form"
import { useErrorDialog } from "@/shared/components/ErrorDialog"
import { Button } from "@/shared/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form"
import { Input } from "@/shared/components/ui/input"
import { ApiError } from "@/shared/utils/apiError"
import { useCreateChildMutation, useRedeemInviteMutation } from "./api/children.mutations"
import { ChildForm } from "./ChildForm"
import {
  type ChildFormValues,
  type RedeemInput,
  type RedeemValues,
  redeemSchema,
} from "./children.schemas"
import { selectChild } from "./useSelectedChild"

type Mode = "choose" | "new" | "code"

interface AddChildDialogProps {
  // Omit when driving via open/onOpenChange.
  trigger?: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function AddChildDialog({ trigger, open: openProp, onOpenChange }: AddChildDialogProps) {
  const [openState, setOpenState] = useState(false)
  const [mode, setMode] = useState<Mode>("choose")

  const open = openProp ?? openState
  const setOpen = (next: boolean) => {
    onOpenChange ? onOpenChange(next) : setOpenState(next)
    if (!next) setMode("choose")
  }

  const close = () => setOpen(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Lägg till barnprofil</DialogTitle>
        </DialogHeader>
        {mode === "choose" && (
          <div className="flex flex-col gap-2">
            <Button variant="outline" className="justify-start" onClick={() => setMode("new")}>
              <Baby />
              Nytt barn
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => setMode("code")}>
              <KeyRound />
              Har en kod
            </Button>
          </div>
        )}
        {mode === "new" && <NewChildForm onDone={close} />}
        {mode === "code" && <RedeemCodeForm onDone={close} />}
      </DialogContent>
    </Dialog>
  )
}

function NewChildForm({ onDone }: { onDone: () => void }) {
  const { showApiError } = useErrorDialog()
  const create = useCreateChildMutation()

  const onSubmit = (values: ChildFormValues) => {
    create.mutate(values, {
      onSuccess: (child) => {
        selectChild(child.id)
        onDone()
      },
      onError: (err) => showApiError(err, { title: "Kunde inte lägga till barnprofil" }),
    })
  }

  return (
    <ChildForm
      defaultValues={{ name: "", birthDate: "", sex: "boy" }}
      onSubmit={onSubmit}
      isPending={create.isPending}
      submitLabel="Lägg till"
    />
  )
}

function RedeemCodeForm({ onDone }: { onDone: () => void }) {
  const { showApiError } = useErrorDialog()
  const redeem = useRedeemInviteMutation()

  const form = useForm<RedeemInput, unknown, RedeemValues>({
    resolver: zodResolver(redeemSchema),
    defaultValues: { code: "" },
  })

  const onSubmit = (values: RedeemValues) => {
    redeem.mutate(values, {
      onSuccess: (child) => {
        selectChild(child.id)
        onDone()
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 400) {
          form.setError("code", { message: "Ogiltig eller använd kod" })
          return
        }
        showApiError(err, { title: "Kunde inte lösa in koden" })
      },
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Inbjudningskod</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  autoCapitalize="characters"
                  className="text-center font-mono text-lg tracking-widest uppercase"
                  maxLength={6}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full" isLoading={redeem.isPending}>
          Lägg till
        </Button>
      </form>
    </Form>
  )
}
